import React, { createContext, useContext, useState, useEffect } from "react";
import { postAuthenticatedRequest } from "../../../utils/api/requests/request";
import { useNotification } from "../../../store/context/notification-context";
import {
  LimitTransactionType,
  LimitTypeEnum,
} from "../../../shared/constants/constants";
const LimitManagementContext = createContext();

export const useLimitManagement = () => useContext(LimitManagementContext);

export const LimitManagementProvider = ({ children }) => {
  const [limitSource, setLimitSource] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [formData, setFormData] = useState({
    account: null,
    type: LimitTypeEnum.TRANSACTION_LIMIT.value,
  });
  const { triggerNotification } = useNotification();

  const handleUpdateLimit = async (data, mode, callBack) => {
    const isDaily = mode === LimitTypeEnum.DAILY_LIMIT.value;
    console.log("data from handleupdate limit is", data);
    try {
      setIsUpdating(true);

      const requestData = {
        accountNumber: formData.account,
        limits: Object.entries(data).map(([limitName, limitValue]) => ({
          transactionType: getTransactionType(limitName),
          transactionLimit: isDaily
            ? limitSource.find(
                (item) =>
                  item?.transactionType === getTransactionType(limitName)
              )?.userTransactionLimit ||
              limitSource.find(
                (item) =>
                  item?.transactionType === getTransactionType(limitName)
              )?.defaultTransactionLimit
            : limitValue,
          dailyLimit: isDaily
            ? limitValue
            : limitSource.find(
                (item) =>
                  item?.transactionType === getTransactionType(limitName)
              )?.userDailyLimit ||
              limitSource.find(
                (item) =>
                  item?.transactionType === getTransactionType(limitName)
              )?.defaultDailyLimit,
        })),
      };
      console.log("requestData from handle update limit is", requestData);
      const response = await postAuthenticatedRequest(
        "LimitUpdateRequestCommand",
        requestData
      );

      if (response?.outData?.isSuccessful) {
        triggerNotification({
          type: "success",
          message: "Success",
          description:
            response?.outData?.message || "Limit updated successfully",
          duration: 5,
        });

        // Refresh the limits after successful update
        await handleLimitRetrieval();
        callBack();
      } else {
        triggerNotification({
          type: "error",
          message: "Error",
          description: response?.outData?.message || "Failed to update limit",
        });
        return false;
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Error",
        description:
          error?.toString() || "An error occurred while updating limit",
      });
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  // Fetch limits when account changes
  const handleLimitRetrieval = async () => {
    try {
      setIsLoading(true);

      const response = await postAuthenticatedRequest("GetAccountLimitQuery", {
        operation: "GET",
        accountNumber: formData.account,
      });

      const result = response?.outData;

      if (result?.isSuccessful) {
        const transformedLimitSource = result?.data?.accountLimits?.map(
          (limit) => ({
            name: Object.values(LimitTransactionType).find(
              (option) => option.value === limit?.transactionType
            ).description,
            transactionType: limit?.transactionType,
            defaultTransactionLimit: limit?.defaultLimit?.transactionLimit,
            defaultDailyLimit: limit?.defaultLimit?.dailyLimit,
            userTransactionLimit: limit?.userLimit?.transactionLimit,
            userDailyLimit: limit?.userLimit?.dailyLimit,
            globalTransactionLimit: limit?.globalLimit?.transactionLimit,
            globalDailyLimit: limit?.globalLimit?.dailyLimit,
          })
        );
        console.log("transformedLimitSource", transformedLimitSource);
        setLimitSource(transformedLimitSource);

        triggerNotification({
          type: "success",
          message: "Success",
          description: result?.message || "Limits retrieved successfully",
          duration: 5,
        });
      } else {
        triggerNotification({
          type: "error",
          message: "Error",
          description:
            result?.message || "An error occurred. Please try again later",
        });
      }
    } catch (error) {
      triggerNotification({
        type: "error",
        message: "Limit Retrieval Failed",
        description:
          error?.toString() || "An error occurred. Please try again later",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getTransactionType = (limitName) => {
    switch (limitName) {
      case "limitAirtime":
        return 0; // Airtime
      case "limitData":
        return 1; // Data
      case "limitBill":
        return 2; // Bills
      case "limitTransfer":
        return 3; // Transfer
      default:
        return;
    }
  };

  const handleSelectChange = (value, name) => {
    setFormData((prevFormData) => {
      const newFormData = { ...prevFormData, [name]: value };

      return newFormData;
    });
  };

  useEffect(() => {
    if (formData.account) handleLimitRetrieval();
  }, [formData.account]);

  return (
    <LimitManagementContext.Provider
      value={{
        limitSource,
        isLoading,
        handleUpdateLimit,
        formData,
        isUpdating,
        handleSelectChange,
        setFormData,
        handleLimitRetrieval,
      }}
    >
      {children}
    </LimitManagementContext.Provider>
  );
};
